import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  fetchMapList,
  likeMap,
  unlikeMap,
  type MapSummary,
} from '../../apiClient/maps';
import { useAuth } from '../../auth/AuthProvider';
import {
  chartDurationSeconds,
  formatDuration,
  stripVariantSuffix,
} from '@shared/chart';
import { parseCirclesVariantSlug, variantLabel } from '@shared/gameTypes';
import { saveModePref } from '../../game/modePref';
import { modePath } from '../../game/modeRouting';
import '../browse.css';
import '../play.css';

type SortOption = 'likes' | 'recent' | 'difficulty' | 'plays';

/**
 * Circles-mode browse page. Functionally identical to the Pulse browse
 * page but pinned to `mode=circles` on the API request and with no
 * "Make your own" action — Circles ships without a UGC editor; the
 * catalogue is the 12 osu-style charts seeded from the same NCS tracks
 * Pulse uses.
 */
export function CirclesBrowsePage() {
  const { user } = useAuth();
  const [maps, setMaps] = useState<MapSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sort, setSort] = useState<SortOption>('likes');
  const [search, setSearch] = useState('');
  const [difficulty, setDifficulty] = useState<number | undefined>(undefined);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    saveModePref('circles');
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchMapList({
        mode: 'circles',
        sort,
        search: debouncedSearch || undefined,
        difficulty,
        // Circles only has the 36 builtins for now — no point in offering
        // a "show official" toggle until UGC lands.
        includeBuiltin: true,
        limit: 60,
      });
      setMaps(res.maps);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load maps');
    } finally {
      setLoading(false);
    }
  }, [sort, debouncedSearch, difficulty]);

  useEffect(() => {
    void load();
  }, [load]);

  const onToggleLike = async (m: MapSummary) => {
    const optimistic = !m.liked;
    setMaps((prev) =>
      prev.map((x) =>
        x.id === m.id
          ? { ...x, liked: optimistic, likeCount: x.likeCount + (optimistic ? 1 : -1) }
          : x,
      ),
    );
    try {
      const fn = optimistic ? likeMap : unlikeMap;
      const result = await fn(m.id);
      setMaps((prev) =>
        prev.map((x) =>
          x.id === m.id ? { ...x, liked: result.liked, likeCount: result.count } : x,
        ),
      );
    } catch (err) {
      console.warn('Like toggle failed:', err);
      setMaps((prev) =>
        prev.map((x) =>
          x.id === m.id ? { ...x, liked: m.liked, likeCount: m.likeCount } : x,
        ),
      );
      setError(err instanceof Error ? err.message : 'Failed to update like');
    }
  };

  const filtered = useMemo(() => maps, [maps]);
  const hasActiveFilters =
    debouncedSearch.length > 0 || difficulty !== undefined || sort !== 'likes';

  return (
    <div className="browse-page" data-testid="circles-browse-page">
      <header className="browse-header">
        <div>
          <h2>Browse Circles maps</h2>
          <p className="muted">{filtered.length} maps</p>
        </div>
        <Link to={modePath('circles')} className="btn btn-ghost">
          Featured tracks
        </Link>
      </header>

      <p className="browse-intro muted">
        Free osu-style Circles maps with custom songs — community charts you can
        play in your browser, no download.
      </p>

      <div className="browse-controls card">
        <input
          type="text"
          placeholder="Search title or description..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="browse-search"
          aria-label="Search maps by title or description"
          data-testid="circles-browse-search"
        />
        <div className="browse-filter-group">
          <label className="muted" htmlFor="circles-sort-select">
            Sort
          </label>
          <select
            id="circles-sort-select"
            value={sort}
            onChange={(e) => setSort(e.target.value as SortOption)}
            data-testid="circles-browse-sort"
          >
            <option value="likes">Most liked</option>
            <option value="recent">Newest</option>
            <option value="plays">Most played</option>
            <option value="difficulty">Hardest</option>
          </select>
        </div>
        <div className="browse-filter-group">
          <label className="muted" htmlFor="circles-diff-select">
            Difficulty
          </label>
          <select
            id="circles-diff-select"
            value={difficulty ?? 'any'}
            onChange={(e) =>
              setDifficulty(e.target.value === 'any' ? undefined : Number(e.target.value))
            }
            data-testid="circles-browse-difficulty"
          >
            <option value="any">Any</option>
            <option value="1">1</option>
            <option value="2">2</option>
            <option value="3">3</option>
            <option value="4">4</option>
            <option value="5">5</option>
          </select>
        </div>
      </div>

      {error && <div className="error-box">{error}</div>}
      {loading && filtered.length === 0 && (
        <div className="browse-grid" data-testid="circles-browse-skeleton">
          {Array.from({ length: 6 }, (_, i) => (
            <div key={i} className="browse-card browse-card-skeleton">
              <div className="browse-card-thumb" />
              <div className="browse-card-body">
                <div className="skeleton-line skeleton-title" />
                <div className="skeleton-line skeleton-meta" />
                <div className="skeleton-line skeleton-desc" />
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="browse-grid">
        {filtered.map((m) => {
          // Variant pills only ever apply to builtins. Circles ships
          // a fixed catalogue today so this is mostly defensive, but
          // we mirror the Pulse page's `isBuiltin` gate so a future
          // UGC-Circles map titled "Going Hard" can't slug-suffix-match
          // its way into a wrongly-labelled HARD pill.
          const variant = m.isBuiltin ? parseCirclesVariantSlug(m.slug).variant : null;
          const bareTitle = variant
            ? stripVariantSuffix({ title: m.title, variant })
            : m.title;
          return (
          <article
            key={m.id}
            className="browse-card"
            data-testid={`circles-browse-card-${m.slug}`}
            style={
              {
                '--map-bg': m.palette.bg,
                '--map-bg-accent': m.palette.bgAccent,
                '--map-pad': m.palette.pad,
                '--map-player': m.palette.player,
              } as React.CSSProperties
            }
          >
            <Link
              to={modePath('circles', `play/${m.slug}`)}
              className="browse-card-thumb"
            >
              <div className="thumb-bg" />
              {/* Three concentric ring placeholders to telegraph the
                  Circles aesthetic on browse cards (vs Pulse's two-rail
                  preview). */}
              <div className="thumb-rail thumb-rail-top" />
              <div className="thumb-rail thumb-rail-bottom" />
              <div className="thumb-player" />
              {m.isBuiltin && <span className="thumb-badge">Official</span>}
            </Link>
            <div className="browse-card-body">
              <div className="browse-card-row">
                <Link
                  to={modePath('circles', `play/${m.slug}`)}
                  className="browse-card-title"
                >
                  {bareTitle}
                  {variant && (
                    <>
                      {' '}
                      <span
                        className={`variant-badge variant-badge-sm variant-badge-${variant}`}
                        data-testid={`circles-browse-variant-${m.slug}`}
                      >
                        {variantLabel(variant).toUpperCase()}
                      </span>
                    </>
                  )}
                </Link>
                <button
                  type="button"
                  className={`like-btn ${m.liked ? 'liked' : ''}`}
                  onClick={() => void onToggleLike(m)}
                  aria-label={m.liked ? 'Unlike' : 'Like'}
                  aria-pressed={m.liked}
                  data-testid={`circles-like-${m.slug}`}
                >
                  <span className="heart">{m.liked ? '\u2665' : '\u2661'}</span>
                  <span>{m.likeCount.toLocaleString()}</span>
                </button>
              </div>
              <div className="browse-card-meta">
                <span>{m.bpm.toFixed(0)} BPM</span>
                <span title="Duration">
                  {formatDuration(chartDurationSeconds(m.bpm, m.totalBeats))}
                </span>
                <span className="diff-pips">
                  {Array.from({ length: 5 }, (_, i) => (
                    <span key={i} className={i < m.difficulty ? 'pip pip-on' : 'pip'} />
                  ))}
                </span>
                <span className="muted">{m.playCount.toLocaleString()} plays</span>
              </div>
              {m.description && <p className="browse-card-desc">{m.description}</p>}
              {/*
                Author tag for non-builtin entries. Circles has no UGC
                editor today so this only fires for any future imports
                a maintainer adds outside the seed pipeline.
              */}
              {!m.isBuiltin && user?.uid === m.authorUid && (
                <span className="muted faint browse-edit-link">Your import</span>
              )}
            </div>
          </article>
          );
        })}
      </div>

      {!loading && filtered.length === 0 && (
        <div className="empty-state">
          {error ? (
            <>
              <p className="muted">Couldn&apos;t load maps right now.</p>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => void load()}
                data-testid="circles-retry-load"
              >
                Try again
              </button>
            </>
          ) : (
            <>
              <p className="muted">No Circles maps match your filters.</p>
              {hasActiveFilters && (
                <button
                  type="button"
                  className="btn"
                  onClick={() => {
                    setSearch('');
                    setDifficulty(undefined);
                    setSort('likes');
                  }}
                  data-testid="circles-clear-filters"
                >
                  Clear filters
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
